import { GridCellParams, GridRowId } from '@mui/x-data-grid-premium';
import { createContext, PropsWithChildren, useContext, useRef } from 'react';
import { createStore, StateCreator, StoreApi, useStore } from 'zustand';

type GridKeyExpansionObj = Partial<Record<GridRowId, boolean>>;
interface GridCoreState {
  hoveredRowId: GridRowId | null;
  hoveredField: string | null;
  setHoveredRowId: (gridId: GridRowId | null) => void;
  setHoveredField: (field: string | null) => void;
  expandedGroups: GridKeyExpansionObj;
  setExpandedGroups: (groups: GridKeyExpansionObj) => void;
  expandedDetails: GridKeyExpansionObj;
  getExpandedDetails: () => GridKeyExpansionObj;
  toggleExpandedDetails: (details: GridRowId) => GridRowId[];
  setExpandedDetails: (details: GridRowId[]) => void;
  setGroupExpanded: (group: GridRowId, value: boolean) => void;
  isDetailsExpanded: (group: GridRowId) => boolean;
  /**
   * This row id is used by `getRowHeight` to determine whether to set the "Show More" row height to 'auto'
   */
  showMoreRowId: GridRowId | null;
  isHoveringCell: (cellParams: GridCellParams) => boolean;
  toggleShowMore: (rowId: GridRowId) => void;
}

const initializeGridCoreStore: StateCreator<GridCoreState> = (set, get) => ({
  hoveredRowId: null,
  setHoveredRowId: (gridId) => set({ hoveredRowId: gridId }),
  setHoveredField: (field) => set({ hoveredField: field }),
  hoveredField: null,
  expandedGroups: {},
  showMoreRowId: null,
  setExpandedGroups: (groups) => set({ expandedGroups: groups }),
  isDetailsExpanded: (group) => get().expandedDetails[group] ?? false,
  setGroupExpanded: (group, shouldExpand) =>
    set((state) => {
      return { expandedGroups: { ...state.expandedGroups, [group]: shouldExpand } };
    }),
  isHoveringCell: (cellParams) => cellParams.id === get().hoveredRowId && cellParams.field === get().hoveredField,
  toggleShowMore: (rowId) => {
    set((state) => {
      return { showMoreRowId: state.showMoreRowId === rowId ? null : rowId };
    });
  },
  getExpandedDetails: () => get().expandedDetails,
  setExpandedDetails: (details) =>
    set({ expandedDetails: Object.fromEntries(details.map((detail) => [detail, true])) }),
  expandedDetails: {},
  toggleExpandedDetails: (details) => {
    const currentExpandedDetails = get().expandedDetails;
    const expandedDetails = { ...currentExpandedDetails, [details]: !currentExpandedDetails[details] };

    set(() => {
      return { expandedDetails: expandedDetails };
    });

    return Object.entries(expandedDetails)
      .filter(([_, value]) => value)
      .map(([key]) => key);
  },
});

const GridCoreContext = createContext<StoreApi<GridCoreState> | null>(null);

export default function GridCoreProvider({ children }: PropsWithChildren) {
  // Reuse an existing store from a parent provider if one is present so that
  // outer components (e.g. custom grid wrappers) can share expansion state
  // with the inner DataGrid. Otherwise create a fresh store.
  const existingStore = useContext(GridCoreContext);
  const storeRef = useRef(existingStore ?? createStore(initializeGridCoreStore));

  return <GridCoreContext.Provider value={storeRef.current}>{children}</GridCoreContext.Provider>;
}

export function useGridCore<T>(selector: (state: GridCoreState) => T): T {
  const store = useContext(GridCoreContext);

  if (!store) {
    throw new Error('Missing GridCoreProvider');
  }

  return useStore(store, selector);
}
